/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import get from 'lodash/get';
import { useParams } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import Form from 'carbon-react/lib/components/form';
import { Option, Select } from 'carbon-react/lib/components/select';
import Textbox from 'carbon-react/lib/components/textbox';

import {
  AvailableLineDataRowFormulas,
  AvailableLineDataTemplateFormulas,
  ReportingTemplateFormula,
  RowFormula as RowFormulaModel
} from '@futrli-sdk/predict';
import { ERROR_STATUS, getErrorStatus } from '@futrli/errors';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { UILoader } from '@futrli/ui-components';

import { ActionButtons } from './action-buttons';
import { CARD_TYPES } from '../../types';
import { CATEGORY_IDS, ROW_FORMULA_GROUP_IDS } from './const';
import { checkFormulaValidation } from './utils';
import { createNewRowFormula, deleteRowFormula, updateRowFormula } from '../../../core/api/reporting/report-builder';
import {
  createNewTemplateFormula,
  deleteTemplateFormula,
  fetchAvailableTemplateFormulaLineData,
  updateTemplateFormula
} from '../../../core/api/reporting/report-templates';
import { deLocalizeFormula, localizeFormula, validateLocalizedFormula } from '../../../utils/formulas/i18n-utils';
import { DeleteItemModal } from '../delete-item-modal/delete-item-modal';
import { fetchAvailableRowFormulaLineData } from '../../../core/api/financial-config';
import {
  FormulaEditorLexical,
  FormulaEditorLexicalHandle
} from '../../../ui/data-builder/formula-content-form/formula-editor-lexical/formula-editor';
import { FormulaReferenceOption } from '../../../utils/formulas/types';
import { getConsolidationShortCode } from '../../utils-data';
import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import { ReportingSidebar } from '../reporting-sidebar/reporting-sidebar';
import { RowFormulaError } from './row-formula-error';
import { trackEvent } from '../../../core/tracker';
import { TRACKING_EVENTS } from '../../../core/constants';
import { useConsolidatedForecastingContext } from '../../../forecast/detail/context/consolidated-forecasting-context';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';
import { useReportBuilderContext } from '../../report-builder.context';
import { useRowFormulas } from '../../hooks/use-row-formulas/use-row-formulas';

import * as styles from './row-formula.scss';

interface Props {
  isOpenDialog?: boolean;
  handleCloseDialog: () => void;
  initialRowFormula?: RowFormulaModel | ReportingTemplateFormula;
  onFormulaCreated?: (f: RowFormulaModel, refToReplace?: string) => void;
}

type FormulaGroup = 'sales' | 'expenses' | 'profit' | 'balance_sheet' | 'cash_flow' | 'people' | 'ratios';
type DisplayAs = 'currency' | 'percentage' | 'number';
type VarianceAnalysis = 'positive' | 'negative';

export const RowFormula = ({ isOpenDialog, handleCloseDialog, initialRowFormula, onFormulaCreated }: Props) => {
  const { isConsolidation } = usePredictionsInfoContext();
  const { consolidation } = useConsolidatedForecastingContext();
  const { getAvailableRowFormulaLineData } = useRowFormulas();

  // Formula builder action controls
  const [formulaName, setFormulaName] = useState('');
  const [formulaGroup, setFormulaGroup] = useState('');
  const [displayAs, setDisplayAs] = useState('currency');
  const [varianceAnalysis, setVarianceAnalysis] = useState('positive');

  // Formula editor
  const lexicalEditorRef = useRef<FormulaEditorLexicalHandle>(null);
  const [formulaContent, setFormulaContent] = useState('');
  const [formulaTouched, setFormulaTouched] = useState(false);
  const [formulaErrorCode, setFormulaErrorCode] = useState<string>();

  // Fetched suggestions for mentions dropdown
  const [initialSuggestions, setInitialSuggestions] = useState<FormulaReferenceOption[] | undefined>();
  const [suggestions, setSuggestions] = useState<FormulaReferenceOption[] | undefined>();

  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const {
    financialConfig,
    getRowFormulas,
    removeRowFormula,
    getFinDataForLiveReport,
    pageLayoutData,
    isTemplate,
    isWorkspaceTemplate,
    dataReferenceMapping,
    allFinFieldsArray,
    updateFormulaNameInReport
  } = useReportBuilderContext();
  const isFutrliTemplate = isTemplate && !isWorkspaceTemplate;

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();

  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';

  const { addErrorMessage } = useContext(PredictManagementContext);

  
  const disableFormulaEditor = (initialRowFormula as ReportingTemplateFormula | undefined)?.kind && !isFutrliTemplate;

  const lexicalInitialContentOptions = useMemo<FormulaReferenceOption[]>(() => {
    const options: FormulaReferenceOption[] = [...(initialSuggestions ?? [])];
    const optionIds = new Set(options.map(option => String(option.id)));

    Object.entries(dataReferenceMapping?.line_data_references ?? {}).forEach(([id, field]) => {
      if (optionIds.has(id)) {
        return;
      }

      const name = field.data.organisation_short_code
        ? `${field.name} - ${field.data.organisation_short_code}`
        : field.name;
      const invalid = !allFinFieldsArray.find(financialField =>
        financialField.organisation_id
          ? `${financialField.name} - ${getConsolidationShortCode(consolidation, financialField.organisation_id)}` === name
          : financialField.name === name
      );

      options.push({ id, invalid, name });
    });

    return options;
  }, [allFinFieldsArray, consolidation, dataReferenceMapping?.line_data_references, initialSuggestions]);

  useEffect(() => {
    if (initialRowFormula && initialSuggestions) {
      const localizedExpression = localizeFormula(initialRowFormula.expression);

      setFormulaName(initialRowFormula.name);
      setFormulaGroup(initialRowFormula.group);
      setDisplayAs(initialRowFormula.output_type);
      setVarianceAnalysis(initialRowFormula.variance_analysis);
      setFormulaContent(localizedExpression);
      setFormulaErrorCode(undefined);
      setFormulaTouched(true);
    }
  }, [dataReferenceMapping, initialRowFormula, initialSuggestions]);

  useEffect(() => {
    const fetchData = async () => {
      if (isOpenDialog && financialConfig) {
        try {
          setIsLoading(true);
          const fetchedAvailableRowFormulaLineData =
            isFutrliTemplate || disableFormulaEditor
              ? await fetchAvailableTemplateFormulaLineData(
                  workspaceId,
                  organisationId,
                  isConsolidation
                )
              : await fetchAvailableRowFormulaLineData(
                  workspaceId,
                  organisationId,
                  isConsolidation
                );
          const parsedInitialOptions = getAvailableRowFormulaLineData(
            fetchedAvailableRowFormulaLineData.profit_and_loss,
            isFutrliTemplate || disableFormulaEditor
              ? (fetchedAvailableRowFormulaLineData as AvailableLineDataTemplateFormulas).futrli_template_formulas
              : (fetchedAvailableRowFormulaLineData as AvailableLineDataRowFormulas).row_formulas,
            consolidation
          );

          if (!initialRowFormula) {
            setInitialSuggestions(parsedInitialOptions);
            setSuggestions(parsedInitialOptions);
          } else {
            const filteredInitialOptions = parsedInitialOptions.filter(o => o.id.indexOf(initialRowFormula.id) < 0);
            setInitialSuggestions(filteredInitialOptions);
            setSuggestions(filteredInitialOptions);
          }
        } catch (e) {
          addErrorMessage?.(e as Error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, organisationId, isOpenDialog, isConsolidation, consolidation, initialRowFormula]);

  const handleFormulaNameChange = (e: React.ChangeEvent<HTMLInputElement>) => setFormulaName(e.target.value);

  const handleFormulaGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => setFormulaGroup(e.target.value);

  const handleDisplayAsChange = (e: React.ChangeEvent<HTMLInputElement>) => setDisplayAs(e.target.value);

  const handleVarianceAnalysisChange = (e: React.ChangeEvent<HTMLInputElement>) => setVarianceAnalysis(e.target.value);

  const handleActionClick = (action: string) => {
    const localizedAction = localizeFormula(action);
    lexicalEditorRef.current?.insertTextAtCursor(localizedAction);
  };

  const handleLexicalFormulaChange = useCallback((content: string) => {
    if (content !== '\n') {
      setFormulaErrorCode(undefined);
      setFormulaTouched(true);
    }
    setFormulaContent(content);
  }, []);

  const handleSaveClick = async () => {
    try {
      if (formulaGroup && formulaName && financialConfig) {
        setIsLoading(true);
        const data = {
          name: formulaName,
          expression: deLocalizeFormula(formulaContent),
          group: formulaGroup as FormulaGroup,
          variance_analysis: varianceAnalysis as VarianceAnalysis,
          output_type: displayAs as DisplayAs
        };

        if (initialRowFormula?.id) {
          isFutrliTemplate
            ? await updateTemplateFormula(
                workspaceId,
                organisationId,
                initialRowFormula.id,
                data,
                isConsolidation
              )
            : await updateRowFormula(
                workspaceId,
                organisationId,
                initialRowFormula.id,
                data,
                isConsolidation
              );
          let formulaIsUsed = false;
          pageLayoutData.forEach(d => {
            const widgetData = get(d, CARD_TYPES.WIDGET);
            if (widgetData) {
              const rowFormulaFinFields = widgetData.finFieldsOptions.find(o => o.ref === initialRowFormula.ref);
              const rowFormulaSelectedFinFields = widgetData.selectedFinFields.find(
                o => o.ref === initialRowFormula.ref
              );
              if (rowFormulaFinFields || rowFormulaSelectedFinFields) {
                formulaIsUsed = true;
              }
            }
          });
          if (formulaIsUsed) {
            getFinDataForLiveReport();
            initialRowFormula.name !== data.name && updateFormulaNameInReport(initialRowFormula.ref, data.name);
          }
        } else {
          const createdFormula = isFutrliTemplate
            ? await createNewTemplateFormula(
                workspaceId,
                organisationId,
                data,
                isConsolidation
              )
            : await createNewRowFormula(
                workspaceId,
                organisationId,
                data,
                isConsolidation
              );
          trackEvent(TRACKING_EVENTS.CREATED_ROW_FORMULA);
          onFormulaCreated?.(createdFormula, initialRowFormula?.ref);
        }
        getRowFormulas();
        handleCloseDialog();
      }
    } catch (error) {
      if (getErrorStatus(error) === ERROR_STATUS._400) {
        setFormulaErrorCode(get(error, 'error_code'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    try {
      if (initialRowFormula && financialConfig) {
        setIsLoading(true);
        isFutrliTemplate
          ? await deleteTemplateFormula(
              workspaceId,
              organisationId,
              initialRowFormula.id,
              isConsolidation
            )
          : await deleteRowFormula(
              workspaceId,
              organisationId,
              initialRowFormula.id,
              isConsolidation
            );
        removeRowFormula(initialRowFormula.ref);
        getRowFormulas();
        handleCloseDialog();
      }
    } catch (e) {
      // console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDeleteDialog = () => setIsOpenDeleteDialog(true);

  const handleCloseDeleteDialog = () => setIsOpenDeleteDialog(false);

  const intl = useIntl();

  if (isLoading) {
    return (
      <ReportingSidebar
        aria-label={intl.formatMessage({
          id: 'reporting.row_formula_builder',
          defaultMessage: 'Row formula builder'
        })}
        title={intl.formatMessage({
          id: 'reporting.row_formula_builder',
          defaultMessage: 'Row formula builder'
        })}
        open={!!isOpenDialog}
        onCancel={handleCloseDialog}
        sidebarProps={{
          width: '50%'
        }}
      >
        <UILoader />
      </ReportingSidebar>
    );
  }

  if (!suggestions) {
    return null;
  }

  // Check if formula is valid for specific language. If language is French and formula contains word SUM, formula
  // is automatically invalid
  const isValidFormulaByLocale = validateLocalizedFormula(formulaContent);
  const isValidFormulaByWasm = isValidFormulaByLocale
    ? checkFormulaValidation(deLocalizeFormula(formulaContent))
    : false;
  const saveDisabled = !formulaName || !formulaGroup || !isValidFormulaByWasm;

  return (
    <ReportingSidebar
      aria-label={intl.formatMessage({
        id: 'reporting.row_formula_builder',
        defaultMessage: 'Row formula builder'
      })}
      title={intl.formatMessage({
        id: 'reporting.row_formula_builder',
        defaultMessage: 'Row formula builder'
      })}
      open={!!isOpenDialog}
      onCancel={handleCloseDialog}
      sidebarProps={{
        width: '50%'
      }}
    >
      <Form
        onSubmit={e => e.preventDefault()}
        leftSideButtons={
          <>
            <RowFormulaError
              formulaErrorCode={formulaErrorCode}
              isValidFormula={isValidFormulaByWasm}
              touched={formulaTouched}
            />
            {initialRowFormula?.id && !disableFormulaEditor && (
              <Button buttonType="tertiary" className={styles.deleteButton} onClick={handleOpenDeleteDialog}>
                <FormattedMessage id="reporting.delete_formula" defaultMessage="Delete this formula" />
              </Button>
            )}
          </>
        }
        rightSideButtons={
          <div>
            <Button mr={2} onClick={handleCloseDialog}>
              {disableFormulaEditor ? (
                <FormattedMessage id="common.close" defaultMessage="Close" />
              ) : (
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              )}
            </Button>
            {!disableFormulaEditor && (
              <Button buttonType="primary" disabled={saveDisabled} onClick={handleSaveClick}>
                <FormattedMessage id="common.save" defaultMessage="Save" />
              </Button>
            )}
          </div>
        }
        stickyFooter
      >
        <Box display="inline-flex" justifyContent="space-between" width={'100%'}>
          <Box flex={2} mr={1}>
            <Textbox
              label={intl.formatMessage({
                id: 'reporting.formula_name',
                defaultMessage: 'Formula name'
              })}
              placeholder={intl.formatMessage({
                id: 'reporting.enter_formula_name',
                defaultMessage: 'Enter formula name'
              })}
              required={true}
              value={formulaName}
              onChange={handleFormulaNameChange}
            />
          </Box>
          <Box flex={3} mr={1}>
            <Select
              aria-label={intl.formatMessage({
                id: 'reporting.formula_group',
                defaultMessage: 'Formula group'
              })}
              autoComplete="off"
              onChange={handleFormulaGroupChange}
              value={formulaGroup}
              label={intl.formatMessage({
                id: 'reporting.formula_group',
                defaultMessage: 'Formula group'
              })}
            >
              <Option
                value="sales"
                text={intl.formatMessage({
                  id: 'financial_fields.category.sales',
                  defaultMessage: 'Sales'
                })}
              />
              <Option
                value="expenses"
                text={intl.formatMessage({
                  id: 'financial_fields.category.expenses',
                  defaultMessage: 'Expenses'
                })}
              />
              <Option
                value="profit"
                text={intl.formatMessage({
                  id: 'financial_fields.category.profit',
                  defaultMessage: 'Profit'
                })}
              />
              <Option
                value="balance_sheet"
                text={intl.formatMessage({
                  id: 'financial_fields.type.balance_sheet',
                  defaultMessage: 'Balance Sheet'
                })}
              />
              <Option
                value="cash_flow"
                text={intl.formatMessage({
                  id: 'financial_fields.type.cash_flow',
                  defaultMessage: 'Cash Flow'
                })}
              />
              <Option
                value="people"
                text={intl.formatMessage({
                  id: 'reporting.people',
                  defaultMessage: 'People'
                })}
              />
              <Option
                value="ratios"
                text={intl.formatMessage({
                  id: 'reporting.ratios',
                  defaultMessage: 'Ratios'
                })}
              />
            </Select>
          </Box>
          <Box flex={2}>
            <Select
              autoComplete="off"
              onChange={handleDisplayAsChange}
              value={displayAs}
              label={intl.formatMessage({
                id: 'reporting.display_as',
                defaultMessage: 'Display as'
              })}
            >
              <Option
                value="percentage"
                text={intl.formatMessage({
                  id: 'common.percentage',
                  defaultMessage: 'Percentage'
                })}
              />
              <Option
                value="currency"
                text={intl.formatMessage({
                  id: 'common.currency',
                  defaultMessage: 'Currency'
                })}
              />
              <Option
                value="number"
                text={intl.formatMessage({
                  id: 'common.number',
                  defaultMessage: 'Number'
                })}
              />
            </Select>
          </Box>
          <Box flex={4} ml={1}>
            <Select
              autoComplete="off"
              onChange={handleVarianceAnalysisChange}
              value={varianceAnalysis}
              label={intl.formatMessage({
                id: 'reporting.variance_analysis',
                defaultMessage: 'Variance analysis'
              })}
            >
              <Option
                value="positive"
                text={intl.formatMessage({
                  id: 'reporting.positive_result',
                  defaultMessage: 'A positive result is favourable'
                })}
              />
              <Option
                value="negative"
                text={intl.formatMessage({
                  id: 'reporting.negative_result',
                  defaultMessage: 'A negative result is favourable'
                })}
              />
            </Select>
          </Box>
        </Box>
        <FormulaEditorLexical
          ref={lexicalEditorRef}
          ariaLabel="Enter row formula"
          autoFocus={isOpenDialog}
          className={clsx(styles.editor, styles.editorCarbon)}
          editorInputClassName={styles.lexicalEditorInput}
          initialContentOptions={lexicalInitialContentOptions}
          initialEditorOptions={suggestions}
          initialFormulaContent={initialRowFormula ? localizeFormula(initialRowFormula.expression) : ''}
          nonSelectableOptionIds={[...CATEGORY_IDS, ...ROW_FORMULA_GROUP_IDS]}
          onFormulaChange={handleLexicalFormulaChange}
          readOnly={!!disableFormulaEditor}
        />
        {!disableFormulaEditor && <ActionButtons handleActionClick={handleActionClick} />}
      </Form>
      <DeleteItemModal
        isOpen={isOpenDeleteDialog}
        onClose={handleCloseDeleteDialog}
        onDelete={handleDeleteClick}
        titleText={<FormattedMessage id="reporting.delete_row_formula_title" defaultMessage="Delete row formula" />}
        bodyText={
          <FormattedMessage
            id="reporting.are_you_sure_delete_row_formula"
            defaultMessage="Are you sure you want to delete this row formula?"
          />
        }
      />
    </ReportingSidebar>
  );
};
